import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { LEProductAccessRowSelectionState } from '../components/InviteTray/LEProductAccessRow';
import type { MemberRole, MemberRoleMap, MemberRolesForEntity } from '../utils/constants';

import { getDefaultRoleValues } from './useRoles';

export type ProductKey = string;
export type LegalEntityId = string;
export type ProductSelections = Partial<Record<ProductKey, MemberRole[]>>;
export type EntityIdToProductSelections = Record<LegalEntityId, ProductSelections>;

type UseMultiLEProductSelectionParams = {
  legalEntityIds: readonly LegalEntityId[];
  rolesByProductPerEntity: Record<LegalEntityId, Record<ProductKey, MemberRoleMap[]>>;
  initialEntitySelections?: EntityIdToProductSelections;
  /**
   * Changing this re-seeds the selection from `initialEntitySelections`,
   * dropping unsaved edits. Used after the member's roles change underneath the
   * tray (e.g. "Revoke admin access"), so a later save can't resubmit stale roles.
   */
  resetKey?: number;
};

export type UseMultiLEProductSelectionReturn = {
  getProductSelectionForEntity: (entityId: LegalEntityId) => LEProductAccessRowSelectionState;
  getRolesForEntity: (entityId: LegalEntityId) => ProductSelections;
  getLegalEntityRoles: () => MemberRolesForEntity[];
  hasAnySelection: boolean;
  hasChanges: boolean;
  handleEntitySelectionToggle: (entityId: LegalEntityId, isSelected: boolean) => void;
  handleProductCheckedChange: (
    entityId: LegalEntityId,
    productKey: ProductKey,
    isChecked: boolean,
  ) => void;
  handleProductRoleToggle: (
    entityId: LegalEntityId,
    productKey: ProductKey,
    role: MemberRole,
    isSelected: boolean,
  ) => void;
  handleSelectAllEntities: () => void;
  handleDeselectAllEntities: () => void;
};

const EMPTY_PRODUCT_SELECTIONS: ProductSelections = {};

function createEmptyEntitySelections(
  legalEntityIds: readonly LegalEntityId[],
): EntityIdToProductSelections {
  return Object.fromEntries(legalEntityIds.map((entityId) => [entityId, {}]));
}

// Seeds only the first product so the entity row shows as 'indeterminate'
// the moment the user toggles an entity on. Picking all products would be
// presumptuous; picking none would leave the row visually unchanged.
function getDefaultEntityProductSelection(
  rolesByProduct: Record<ProductKey, MemberRoleMap[]> = {},
): ProductSelections {
  const defaultProductKey = Object.keys(rolesByProduct).find(
    (productKey) => (rolesByProduct[productKey]?.length ?? 0) > 0,
  );

  if (!defaultProductKey) {
    return {};
  }

  const defaultRoleValues = getDefaultRoleValues(rolesByProduct, defaultProductKey);
  return defaultRoleValues.length ? { [defaultProductKey]: defaultRoleValues } : {};
}

function entitySelectionsEqual(
  a: EntityIdToProductSelections,
  b: EntityIdToProductSelections,
): boolean {
  const allEntityIds = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const entityId of allEntityIds) {
    const aProducts = a[entityId] ?? {};
    const bProducts = b[entityId] ?? {};
    const allProductKeys = new Set([...Object.keys(aProducts), ...Object.keys(bProducts)]);
    for (const productKey of allProductKeys) {
      const aRoles = new Set(aProducts[productKey] ?? []);
      const bRoles = new Set(bProducts[productKey] ?? []);
      if (aRoles.size !== bRoles.size) return false;
      for (const role of aRoles) {
        if (!bRoles.has(role)) return false;
      }
    }
  }
  return true;
}

function setProductRoles(
  selections: EntityIdToProductSelections,
  entityId: LegalEntityId,
  productKey: ProductKey,
  nextRoles: MemberRole[],
): EntityIdToProductSelections {
  const previous = selections[entityId] ?? EMPTY_PRODUCT_SELECTIONS;

  if (nextRoles.length === 0) {
    const { [productKey]: _removed, ...next } = previous;
    return { ...selections, [entityId]: next };
  }

  return {
    ...selections,
    [entityId]: { ...previous, [productKey]: nextRoles },
  };
}

export function useMultiLEProductSelection({
  legalEntityIds,
  rolesByProductPerEntity,
  initialEntitySelections,
  resetKey,
}: UseMultiLEProductSelectionParams): UseMultiLEProductSelectionReturn {
  const initialRef = useRef<EntityIdToProductSelections | null>(null);
  const [entityIdToProductSelections, setEntityIdToProductSelections] =
    useState<EntityIdToProductSelections>(() => {
      const initial = {
        ...createEmptyEntitySelections(legalEntityIds),
        ...initialEntitySelections,
      };
      initialRef.current = initial;
      return initial;
    });

  const lastResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (resetKey === lastResetKeyRef.current) return;
    lastResetKeyRef.current = resetKey;
    const initial = {
      ...createEmptyEntitySelections(legalEntityIds),
      ...initialEntitySelections,
    };
    initialRef.current = initial;
    setEntityIdToProductSelections(initial);
  }, [initialEntitySelections, legalEntityIds, resetKey]);

  const hasChanges = useMemo(
    () =>
      initialRef.current !== null &&
      !entitySelectionsEqual(entityIdToProductSelections, initialRef.current),
    [entityIdToProductSelections],
  );

  const handleEntitySelectionToggle = useCallback(
    (entityId: LegalEntityId, isSelected: boolean) => {
      setEntityIdToProductSelections((previous) => ({
        ...previous,
        [entityId]: isSelected
          ? getDefaultEntityProductSelection(rolesByProductPerEntity[entityId])
          : {},
      }));
    },
    [rolesByProductPerEntity],
  );

  const handleProductCheckedChange = useCallback(
    (entityId: LegalEntityId, productKey: ProductKey, isChecked: boolean) => {
      setEntityIdToProductSelections((previous) => {
        const currentSelection = previous[entityId]?.[productKey] ?? [];

        // Unchecking: clear the role list. If it's already empty, nothing to do.
        if (!isChecked) {
          if (currentSelection.length === 0) {
            return previous;
          }
          return setProductRoles(previous, entityId, productKey, []);
        }

        // Checking: don't overwrite an existing pick. Otherwise seed the default.
        if (currentSelection.length > 0) {
          return previous;
        }
        const defaultRoleValues = getDefaultRoleValues(
          rolesByProductPerEntity[entityId] ?? {},
          productKey,
        );
        if (defaultRoleValues.length === 0) {
          return previous;
        }
        return setProductRoles(previous, entityId, productKey, defaultRoleValues);
      });
    },
    [rolesByProductPerEntity],
  );

  const handleProductRoleToggle = useCallback(
    (entityId: LegalEntityId, productKey: ProductKey, role: MemberRole, isSelected: boolean) => {
      setEntityIdToProductSelections((previous) => {
        const currentSelection = previous[entityId]?.[productKey] ?? [];
        const alreadySelected = currentSelection.includes(role);

        // Adding a role.
        if (isSelected) {
          if (alreadySelected) {
            return previous;
          }
          return setProductRoles(previous, entityId, productKey, [...currentSelection, role]);
        }

        // Removing a role.
        if (!alreadySelected) {
          return previous;
        }
        return setProductRoles(
          previous,
          entityId,
          productKey,
          currentSelection.filter((value) => value !== role),
        );
      });
    },
    [],
  );

  const handleSelectAllEntities = useCallback(() => {
    setEntityIdToProductSelections(
      Object.fromEntries(
        legalEntityIds.map((entityId) => [
          entityId,
          getDefaultEntityProductSelection(rolesByProductPerEntity[entityId]),
        ]),
      ),
    );
  }, [legalEntityIds, rolesByProductPerEntity]);

  const handleDeselectAllEntities = useCallback(() => {
    setEntityIdToProductSelections(createEmptyEntitySelections(legalEntityIds));
  }, [legalEntityIds]);

  const hasAnySelection = Object.values(entityIdToProductSelections).some((selection) =>
    Object.values(selection).some((roles) => (roles?.length ?? 0) > 0),
  );

  // Driven by the actual stored roles, not by `productKeys`. We strip empty
  // role lists in `setProductRoles`, so the presence of any value here means
  // the entity has at least one selected product.
  const getProductSelectionForEntity = useCallback(
    (entityId: LegalEntityId): LEProductAccessRowSelectionState => {
      const selection = entityIdToProductSelections[entityId];
      const entityHasSelection = selection
        ? Object.values(selection).some((roles) => (roles?.length ?? 0) > 0)
        : false;
      return entityHasSelection ? 'indeterminate' : 'none';
    },
    [entityIdToProductSelections],
  );

  const getRolesForEntity = useCallback(
    (entityId: LegalEntityId) => entityIdToProductSelections[entityId] ?? EMPTY_PRODUCT_SELECTIONS,
    [entityIdToProductSelections],
  );

  const getLegalEntityRoles = useCallback(
    (): MemberRolesForEntity[] =>
      Object.entries(entityIdToProductSelections).flatMap(([entityId, selection]) => {
        const roles = Object.values(selection).flatMap((entityRoles) => entityRoles ?? []);
        return roles.length > 0 ? [{ legalEntityId: entityId, roles }] : [];
      }),
    [entityIdToProductSelections],
  );

  return {
    getProductSelectionForEntity,
    getRolesForEntity,
    getLegalEntityRoles,
    hasAnySelection,
    hasChanges,
    handleEntitySelectionToggle,
    handleProductCheckedChange,
    handleProductRoleToggle,
    handleSelectAllEntities,
    handleDeselectAllEntities,
  };
}
